import React, { createContext, useContext, useMemo } from 'react';

import { useHttpClientIntegration } from '@/shared/infra/http/HttpClientContext';
import { ApiCaseCloseRepository } from '../repositories/ApiCaseCloseRepository';
import { FetchCaseClosureReasonsUseCase } from '../../application/use-cases/FetchCaseClosureReasonsUseCase';
import { CloseCaseUseCase } from '../../application/use-cases/CloseCaseUseCase';
import { ICaseCloseRepository } from '../../application/ports/outbound/ICaseCloseRepository';

interface CaseCloseDependencies {
  caseCloseRepository: ICaseCloseRepository;
  fetchCaseClosureReasonsUseCase: FetchCaseClosureReasonsUseCase;
  closeCaseUseCase: CloseCaseUseCase;
}

const CaseCloseDIContext = createContext<CaseCloseDependencies | undefined>(undefined);

export const CaseCloseDIProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const httpClient = useHttpClientIntegration();

  const dependencies = useMemo(() => {
    const caseCloseRepository = new ApiCaseCloseRepository(httpClient);
    const fetchCaseClosureReasonsUseCase = new FetchCaseClosureReasonsUseCase(caseCloseRepository);
    const closeCaseUseCase = new CloseCaseUseCase(caseCloseRepository);

    return {
      caseCloseRepository,
      fetchCaseClosureReasonsUseCase,
      closeCaseUseCase,
    };
  }, [httpClient]);

  return <CaseCloseDIContext.Provider value={dependencies}>{children}</CaseCloseDIContext.Provider>;
};

export const useCaseCloseDI = (): CaseCloseDependencies => {
  const context = useContext(CaseCloseDIContext);
  if (!context) {
    throw new Error('useCaseCloseDI must be used within a CaseCloseDIProvider');
  }
  return context;
};
